import {
  AppstoreOutlined,
  DownloadOutlined,
  RedoOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  ATable,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../components';
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../hooks/toast';
import { IInventory } from '../../shared/interfaces';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { Card, Col, Row, Tag } from 'antd';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';

const Inventories: React.FC = () => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { permissionFind } = useAuth();
  const [search, setSearch] = useState<string>('');
  const [inventories, setInventories] = useState<IInventory[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const listInventories = useCallback(async () => {
    setLoading(true);
    setInventories([]);
    try {
      const response = await api.post('/inventories/list', {
        take: limit,
        skip: (page - 1) * limit,
        keyword: search,
      });
      setPages(response.data.count);
      setInventories(response.data.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar o estoque',
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast]);

  const getInventoryStatus = (inventory: IInventory) => {
    const quantity = Number(inventory.quantity || 0);
    const minimum = Number(inventory.product_minimum_inventory || 0);

    if (quantity <= 0) {
      return { label: 'Sem estoque', color: 'red' as const };
    }

    if (quantity <= minimum) {
      return { label: 'Atenção', color: 'orange' as const };
    }

    return { label: 'Normal', color: 'green' as const };
  };

  const inventorySummary = {
    total: inventories.length,
    noStock: inventories.filter((item) => Number(item.quantity || 0) <= 0)
      .length,
    attention: inventories.filter(
      (item) =>
        Number(item.quantity || 0) > 0 &&
        Number(item.quantity || 0) <=
          Number(item.product_minimum_inventory || 0),
    ).length,
  };

  useEffect(() => {
    listInventories();
  }, [page, search, limit, listInventories]);

  return (
    <PageLayout>
      <PageIntro
        badge="Estoque"
        icon={<AppstoreOutlined />}
        title="Saldo em Estoque"
        description="Veja aqui quanto ainda existe de cada item. Quando precisar repor ou retirar manualmente, use os atalhos desta tela."
      />
      <MenuOptions
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => listInventories(),
          },
          permissionFind([PermissionActions.inventoriesEntryCreate])
            ? {
                label: 'Registrar Entrada',
                key: '2',
                icon: <DownloadOutlined />,
                onClick: () => navigate('/inventory_entries'),
              }
            : null,
          permissionFind([PermissionActions.inventoriesOutputCreate])
            ? {
                label: 'Registrar Saída',
                key: '3',
                icon: <UploadOutlined />,
                onClick: () => navigate('/inventory_outputs'),
              }
            : null,
        ]}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />
      <PagePanel>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Card bordered={false}>
              <strong>Produtos listados:</strong> {inventorySummary.total}
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card bordered={false}>
              <strong>Itens em atenção:</strong> {inventorySummary.attention}
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card bordered={false}>
              <strong>Itens sem estoque:</strong> {inventorySummary.noStock}
            </Card>
          </Col>
        </Row>
      </PagePanel>
      <PagePanel>
        <ATable
          scroll={{ y: 510 }}
          columns={[
            {
              title: 'Cód.',
              width: 120,
              dataIndex: 'product_code',
            },
            {
              title: 'Produto',
              dataIndex: 'product_name',
            },
            {
              title: 'Unidade',
              dataIndex: 'product_unity',
            },
            {
              title: 'Mínimo',
              dataIndex: 'product_minimum_inventory',
            },
            {
              title: 'Máximo',
              dataIndex: 'product_maximum_inventory',
            },
            {
              title: 'Saldo atual',
              dataIndex: 'quantity',
            },
            {
              title: 'Situação',
              render: (record: IInventory) => {
                const status = getInventoryStatus(record);

                return <Tag color={status.color}>{status.label}</Tag>;
              },
            },
          ]}
          loading={loading}
          dataSource={inventories}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
    </PageLayout>
  );
};

export default Inventories;
